import { FC, useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import { useApp } from "../../context/AppContext";
import styles from "../../assets/css/preview.module.scss";
import PreviewInsole3DModelMain from "./3DModel/Main";
import { PersonalizationRoute } from "../../constants/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ARCH_HEIGHT_DATA, FOOT_SIZE_DATA } from "../../constants/foot";
import spinner from '../../assets/images/spinner.png';
import { createNavigateWithParams } from "../../helpers/navigation";
import { getArchHeightPercentageWithinPill, getFootSizeSliderPercentageInRange } from "../../helpers/foot";
import { trackPreviewStart, trackPreviewEnd, trackPreviewFormField } from "../../services/google_analytics";

import { preloadAllRibbingGlbs } from "../../constants/glb-assets";
import FoamPackageSelectorMain from "../foam-package-selector/Main";
import range from "lodash/range";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { ConnectionStatus } from "../../hooks/useFirebaseConnection";
import { ArchHeightData } from "../../types/foot";
import StudioSlot from "../insole-studio/StudioSlot";

preloadAllRibbingGlbs();

const PreviewInsole: FC = () => {
  useScrollToTop();
  const {
    setCustomInsoleValues,
    customInsoleValues: {
      leftFootArchHeightData,
      rightFootArchHeightData,
      leftFootLengthInMM,
      rightFootLengthInMM,
      leftFootArchHeightInMM,
      rightFootArchHeightInMM,
      leftFootSizeData,
      rightFootSizeData,
      scanDataConsent,
    },
    selectedFoamPackageOption,
    connectionStatus,
  } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (connectionStatus === ConnectionStatus.CONNECTED) {
      trackPreviewStart();
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (connectionStatus === ConnectionStatus.INITIALIZING_UUID_SESSION) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [connectionStatus]);

  const goToPersonalization = () => {
    if (!areBothFeetScanned) return;
    trackPreviewFormField('scan_data_consent', String(scanDataConsent));
    trackPreviewEnd();
    createNavigateWithParams(navigate, searchParams)(PersonalizationRoute);
  };

  const areBothFeetScanned = (() => {
    return !isEmpty(leftFootArchHeightData) && !isEmpty(rightFootArchHeightData);
  })();

  const handlOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;

    switch (name) {
      case "scanDataConsent":
        setCustomInsoleValues((prev) => ({
          ...prev,
          scanDataConsent: e.currentTarget?.checked,
        }));
        break;
      default:
        break;
    }
  };

  const renderArchHeightSlider = () => {
    const archHeightPillsBuckets = [3,6,9]

    const { type: leftArchType, bucket: leftArchBucket } = leftFootArchHeightData as ArchHeightData;
    const { type: rightArchType, bucket: rightArchBucket } = rightFootArchHeightData as ArchHeightData;

    // Find the single pill each foot's bucket falls into, then compute the percentage once
    const firstPill = archHeightPillsBuckets[0];
    const lastPill  = archHeightPillsBuckets[archHeightPillsBuckets.length - 1];

    const leftPillBucket  = archHeightPillsBuckets.find(p => leftArchBucket  <= p);
    const rightPillBucket = archHeightPillsBuckets.find(p => rightArchBucket <= p);

    const leftPercentageInPill = leftFootArchHeightInMM ? getArchHeightPercentageWithinPill(leftFootArchHeightInMM, leftPillBucket) : 0;
    const rightPercentageInPill = rightFootArchHeightInMM ? getArchHeightPercentageWithinPill(rightFootArchHeightInMM, rightPillBucket) : 0;

    // Shift label text to stay within bounds — only on edge pills:
    //   first pill: correct left-overflow only (positive shift)
    //   last pill:  correct right-overflow only (negative shift)
    //   middle pill: no correction
    const leftTextOffset  = leftPillBucket  === firstPill ? Math.max(0, 40 - leftPercentageInPill)
                          : leftPillBucket  === lastPill  ? Math.min(0, 57 - leftPercentageInPill)
                          : 0;
    const rightTextOffset = rightPillBucket === firstPill ? Math.max(0, 40 - rightPercentageInPill)
                          : rightPillBucket === lastPill  ? Math.min(0, 57 - rightPercentageInPill)
                          : 0;

    return (
      <div className={styles.rangeSliderContainer}>
        <div className={styles.rangeSliderTitle}>Arch Height</div>
        <div className={styles.staticRangeSlider}>
          <div className={styles.staticRangeSliderOptionList}>
            {archHeightPillsBuckets.map((pillBucket) => {
              const showLeftFootPointer = pillBucket === leftPillBucket;
              const showRightFootPointer = pillBucket === rightPillBucket;

              return (
                <div key={pillBucket} className={styles.staticRangeSliderOption}>
                  {ARCH_HEIGHT_DATA[pillBucket.toString()]?.pillText}
                  {showLeftFootPointer && <div className={styles.leftFootPointer} style={{ left: `${leftPercentageInPill}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${leftTextOffset}%))` }}>Left: {leftArchType}</span></div>}
                  {showRightFootPointer && <div className={styles.rightFootPointer} style={{ left: `${rightPercentageInPill}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${rightTextOffset}%))` }}>Right: {rightArchType}</span></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderShoeSizeSlider = () => {
    const largerFootSizeData = leftFootLengthInMM > rightFootLengthInMM ? leftFootSizeData : rightFootSizeData;
    const largerFootSizeDataMen = ('men' in largerFootSizeData) ? largerFootSizeData.men : undefined;
    const largerFootSizeDataWomen = ('women' in largerFootSizeData) ? largerFootSizeData.women : undefined;

    // Calculate size range to always show exactly 5 sizes
    const largerFootSize = ('men' in largerFootSizeData) ? Number(largerFootSizeData.men) : 10;
    const SIZES_TO_SHOW = 5;
    const SIZES_AFTER_LARGER = 2; // Show 2 sizes after the larger foot size

    // Calculate min and max to show 5 sizes total with 2 sizes after the larger foot
    let sizeRangeMinSize = largerFootSize - (SIZES_TO_SHOW - SIZES_AFTER_LARGER - 1);
    let sizeRangeMaxSize = largerFootSize + SIZES_AFTER_LARGER;

    // Clamp to available size range (5-15)
    if (sizeRangeMinSize < Number(FOOT_SIZE_DATA['5'].men)) {
      const offset = Number(FOOT_SIZE_DATA['5'].men) - sizeRangeMinSize;
      sizeRangeMinSize = Number(FOOT_SIZE_DATA['5'].men);
      sizeRangeMaxSize = Math.min(sizeRangeMaxSize + offset, Number(FOOT_SIZE_DATA['15'].men));
    }
    if (sizeRangeMaxSize > Number(FOOT_SIZE_DATA['15'].men)) {
      const offset = sizeRangeMaxSize - Number(FOOT_SIZE_DATA['15'].men);
      sizeRangeMaxSize = Number(FOOT_SIZE_DATA['15'].men);
      sizeRangeMinSize = Math.max(sizeRangeMinSize - offset, Number(FOOT_SIZE_DATA['5'].men));
    }

    const leftFootSizePercentage = getFootSizeSliderPercentageInRange(leftFootLengthInMM, Number(leftFootSizeData?.greaterThanOrEqual), Number(leftFootSizeData?.lessThan));
    const rightFootSizePercentage = getFootSizeSliderPercentageInRange(rightFootLengthInMM, Number(rightFootSizeData?.greaterThanOrEqual), Number(rightFootSizeData?.lessThan));

    const leftFootMenSize = Number(leftFootSizeData?.men);
    const rightFootMenSize = Number(rightFootSizeData?.men);

    const leftShoeSizeTextOffset  = leftFootMenSize  === sizeRangeMinSize ? Math.max(0, 14 - leftFootSizePercentage)
                                  : leftFootMenSize  === sizeRangeMaxSize ? Math.min(0, 76 - leftFootSizePercentage)
                                  : 0;
    const rightShoeSizeTextOffset = rightFootMenSize === sizeRangeMinSize ? Math.max(0, 23 - rightFootSizePercentage)
                                  : rightFootMenSize === sizeRangeMaxSize ? Math.min(0, 76 - rightFootSizePercentage)
                                  : 0;

    return (
      <div className={styles.rangeSliderContainer}>
        <div className={styles.rangeSliderTitle}>Shoe Size</div>
        <div className={styles.rangeSliderSubTitle}>US Women&apos;s</div>
        <div className={styles.staticRangeSlider}>
          <div className={styles.staticRangeSliderOptionList}>
            {range(sizeRangeMinSize, sizeRangeMaxSize + 1).map((menSize) => {
              const sizeData = FOOT_SIZE_DATA[menSize.toString()];
              const showLeftFootPointer = leftFootSizeData?.women === sizeData?.women;
              const showRightFootPointer = rightFootSizeData?.women === sizeData?.women;
              return (
                <div key={`women-${menSize}`} className={`${styles.staticRangeSliderOption} ${largerFootSizeDataWomen === sizeData?.women ? styles.active : ''}`}>
                  {sizeData?.women}
                  {showLeftFootPointer && <div className={styles.leftFootPointer} style={{ left: `${leftFootSizePercentage}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${leftShoeSizeTextOffset}%))` }}>Left</span></div>}
                  {showRightFootPointer && <div className={styles.rightFootPointer} style={{ left: `${rightFootSizePercentage}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${rightShoeSizeTextOffset}%))` }}>Right</span></div>}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.rangeSliderSubTitle}>US Men&apos;s</div>
        <div className={styles.staticRangeSlider}>
          <div className={styles.staticRangeSliderOptionList}>
            {range(sizeRangeMinSize, sizeRangeMaxSize + 1).map((menSize) => {
              const sizeData = FOOT_SIZE_DATA[menSize.toString()];
              const showLeftFootPointer = leftFootSizeData?.men === sizeData?.men;
              const showRightFootPointer = rightFootSizeData?.men === sizeData?.men;
              return (
                <div key={`men-${menSize}`} className={`${styles.staticRangeSliderOption} ${largerFootSizeDataMen === sizeData?.men ? styles.active : ''}`}>
                  {sizeData?.men}
                  {showLeftFootPointer && <div className={styles.leftFootPointer} style={{ left: `${leftFootSizePercentage}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${leftShoeSizeTextOffset}%))` }}>Left</span></div>}
                  {showRightFootPointer && <div className={styles.rightFootPointer} style={{ left: `${rightFootSizePercentage}%` }}><span style={{ display: 'inline-block', transform: `translateX(calc(${rightShoeSizeTextOffset}%))` }}>Right</span></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewDetails = () => {
    return (
      <div className={`${styles.previewFormBody} ${styles.customScrollbar}`}>
        <div className={styles.formSpacer}>
          <h2 className={styles.previewFormTitle}>PREVIEW INSOLES</h2>
          {renderArchHeightSlider()}
          {renderShoeSizeSlider()}
          <div className={styles.formGroup}>
            <label className={styles.customCheckboxContainer}>
              <input
                type="checkbox"
                name="scanDataConsent"
                checked={scanDataConsent}
                onChange={handlOnChange}
                className={styles.customCheckbox}
              />
              <div className={styles.customCheckboxtext}>
                <div className={styles.primaryFontBold}>
                  Save scan data to my profile for reordering
                </div>
                <div>
                  <small>(Previous scan data will be overwritten)</small>
                </div>
              </div>
            </label>

          </div>
        </div>
      </div>
    );
  };

  if (connectionStatus === ConnectionStatus.INITIALIZING_UUID_SESSION) {
    return (
      <div className={styles.spinnerFullScreen}>
        <img src={spinner} alt="loader" className={styles.spinnerAbsoluteImg} />
      </div>
    );
  }

  return (
    <>
      <PreviewInsole3DModelMain
        leftArchIndex={leftFootArchHeightData?.bucket}
        rightArchIndex={rightFootArchHeightData?.bucket}
        selectedFoamPackageOption={selectedFoamPackageOption}
      />
      <StudioSlot name="leftColExtras">
        <FoamPackageSelectorMain />
      </StudioSlot>
      <StudioSlot name="rightCol">
        {renderPreviewDetails()}
      </StudioSlot>
      <StudioSlot name="footer">
        <div className={styles.mlAuto}>
          <button disabled={!areBothFeetScanned} onClick={goToPersonalization} className={`${styles.btnPill} ${styles.btnPrimary}`}>
            <span>Next</span>
            <i className="icon-arrow-right"></i>
          </button>
        </div>
      </StudioSlot>
    </>
  );
};

export default PreviewInsole;
